import { useMemo, useState } from "react";
import { Minus, Plus, Search, X } from "lucide-react";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { ProductIcon } from "@/components/bakery/Art";
import { DateField, Field, inputCls } from "@/components/common/Field";
import PicSelect from "@/components/common/PicSelect";
import { PrimaryBtn } from "@/components/common/ui";
import { api, errMsg } from "@/lib/api";
import { SALES_TYPES, isPackUnit, saleUnitLabel, todayJkt } from "@/lib/format";
import { cn } from "@/lib/utils";

const CATS = ["Semua", "Roti", "Bagel", "Tortilla", "Supplier"];

function Toggle({ options, value, onChange, testid }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((o) => (
        <button key={o} type="button" onClick={() => onChange(o)} data-testid={`${testid}-${o.replace(/\W/g, "").toLowerCase()}`}
          className={cn("press rounded-full border px-3.5 py-2 text-xs font-semibold", value === o ? "border-ink bg-ink text-white" : "border-zinc-300 bg-white hover:border-ink")}>
          {o}
        </button>
      ))}
    </div>
  );
}

function Tiles({ products, cart, onAdd }) {
  const [cat, setCat] = useState("Semua");
  const [q, setQ] = useState("");
  const list = products.filter((p) => (cat === "Semua" || p.category === cat) && (!q || p.name.toLowerCase().includes(q.toLowerCase())));
  return (
    <div>
      <div className="mb-4 flex flex-col gap-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari produk…" className={cn(inputCls, "pl-9")} data-testid="kasir-search" />
        </div>
        <div className="no-scrollbar flex gap-1.5 overflow-x-auto">{CATS.map((c) => (
          <button key={c} onClick={() => setCat(c)} data-testid={`kasir-cat-${c.toLowerCase()}`}
            className={cn("press shrink-0 rounded-md border px-3 py-2 text-xs font-semibold", cat === c ? "border-brand bg-brand text-white" : "border-zinc-300 bg-white")}>{c}</button>
        ))}</div>
      </div>
      <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 xl:grid-cols-4" data-testid="kasir-tiles">
        {list.map((p) => (
          <button key={p.id} onClick={() => onAdd(p.id)} data-testid={`kasir-tile-${p.code}`}
            className={cn("lift press relative flex min-h-[112px] flex-col items-start rounded-lg border-2 bg-white p-3 text-left", cart[p.id] ? "border-brand" : "border-zinc-200 hover:border-ink")}>
            {cart[p.id] > 0 && <span className="num absolute right-2 top-2 grid h-7 min-w-7 place-items-center rounded-full bg-brand px-1.5 text-xs font-bold text-white">{cart[p.id]}</span>}
            <ProductIcon category={p.category} bundle={p.is_bundle} className="h-7 w-7" />
            <span className="mt-2 line-clamp-2 text-sm font-semibold leading-snug">{p.name}</span>
            <span className="mt-auto pt-1 text-[11px] text-zinc-500">{saleUnitLabel(p)}{!p.is_bundle && <> · stok <span className={cn("num", p.stock <= 0 && "font-bold text-brand")}>{p.stock}</span></>}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function deductions(cart, pmap) {
  const out = {};
  Object.entries(cart).forEach(([pid, q]) => {
    const p = pmap[pid];
    if (!p || !q) return;
    const comps = p.is_bundle ? p.bundle_components.map((c) => [c, q]) : [[pid, isPackUnit(p) ? q * p.sales_pack_size : q]];
    comps.forEach(([c, n]) => { out[c] = (out[c] || 0) + n; });
  });
  return Object.entries(out).map(([pid, n]) => ({ name: pmap[pid]?.name, n, unit: pmap[pid]?.stock_unit }));
}

export default function ManualSale({ products, onSaved }) {
  const [date, setDate] = useState(todayJkt());
  const [channel, setChannel] = useState("Offline");
  const [salesType, setSalesType] = useState("Sales");
  const [customer, setCustomer] = useState("");
  const [pic, setPic] = useState("");
  const [notes, setNotes] = useState("");
  const [cart, setCart] = useState({});
  const [saving, setSaving] = useState(false);
  const sellable = (products || []).filter((p) => p.active && p.category !== "Frozen Dough");
  const pmap = useMemo(() => Object.fromEntries((products || []).map((p) => [p.id, p])), [products]);
  const set = (pid, q) => setCart((c) => { const n = { ...c, [pid]: Math.max(0, q) }; if (!n[pid]) delete n[pid]; return n; });
  const items = Object.entries(cart);
  const total = items.reduce((s, [, q]) => s + q, 0);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/sales", { date, channel, sales_type: salesType, customer, pic_user_id: pic, notes, lines: items.map(([product_id, qty]) => ({ product_id, qty })) });
      toast.success(`Penjualan tersimpan · ${data.txn_no}`);
      setCart({}); setCustomer(""); setNotes("");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  return (
    <div className="grid gap-8 lg:grid-cols-[1fr_400px]">
      <Tiles products={sellable} cart={cart} onAdd={(pid) => set(pid, (cart[pid] || 0) + 1)} />
      <div className="lg:sticky lg:top-24 lg:self-start">
        <div className="receipt mt-3 px-5 py-6" data-testid="kasir-receipt">
          <div className="text-center">
            <div className="font-display text-lg font-extrabold tracking-tight">STRONGHOLD BAKERY</div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500">Struk Kasir · Tanpa Harga</div>
          </div>
          <div className="my-4 border-t border-dashed border-zinc-300" />
          <div className="space-y-3">
            <DateField value={date} onChange={setDate} testid="kasir-date" />
            <Field label="Channel" required><Toggle options={["Offline", "WhatsApp", "Shopee", "Tokopedia/TikTok"]} value={channel} onChange={setChannel} testid="kasir-channel" /></Field>
            <Field label="Tipe" required><Toggle options={SALES_TYPES} value={salesType} onChange={setSalesType} testid="kasir-type" /></Field>
            <Field label="Nama customer / penerima"><input value={customer} onChange={(e) => setCustomer(e.target.value)} placeholder="Opsional" className={inputCls} data-testid="kasir-customer" /></Field>
            <PicSelect value={pic} onChange={setPic} testid="kasir-pic" />
          </div>
          <div className="my-4 border-t border-dashed border-zinc-300" />
          {items.length === 0 ? <p className="py-4 text-center text-sm text-zinc-400" data-testid="kasir-cart-empty">Ketuk produk di etalase untuk menambah.</p> : (
            <ul className="space-y-2" data-testid="kasir-cart">
              {items.map(([pid, q]) => (
                <li key={pid} className="flex items-center gap-2 text-sm" data-testid={`kasir-cart-item-${pmap[pid]?.code}`}>
                  <span className="min-w-0 flex-1"><span className="block truncate font-medium">{pmap[pid]?.name}</span><span className="text-[11px] text-zinc-500">{saleUnitLabel(pmap[pid])}</span></span>
                  <button onClick={() => set(pid, q - 1)} className="press grid h-9 w-9 place-items-center rounded border border-zinc-300" data-testid={`kasir-minus-${pmap[pid]?.code}`}><Minus className="h-3.5 w-3.5" /></button>
                  <span className="num w-8 text-center font-bold">{q}</span>
                  <button onClick={() => set(pid, q + 1)} className="press grid h-9 w-9 place-items-center rounded border border-zinc-300" data-testid={`kasir-plus-${pmap[pid]?.code}`}><Plus className="h-3.5 w-3.5" /></button>
                  <button onClick={() => set(pid, 0)} className="press grid h-9 w-7 place-items-center text-zinc-400 hover:text-brand"><X className="h-4 w-4" /></button>
                </li>
              ))}
            </ul>
          )}
          {items.length > 0 && (
            <>
              <div className="my-4 border-t border-dashed border-zinc-300" />
              <div className="flex justify-between font-display text-base font-bold"><span>Total item</span><span className="num" data-testid="kasir-total">{total}</span></div>
              <div className="mt-2 rounded bg-zinc-50 p-2 text-[11px] text-zinc-600" data-testid="kasir-deductions">
                <div className="mb-1 font-semibold uppercase tracking-wider">Pengurangan stok</div>
                {deductions(cart, pmap).map((d) => <div key={d.name} className="flex justify-between"><span className="truncate">{d.name}</span><span className="num text-brand">−{d.n} {d.unit}</span></div>)}
              </div>
            </>
          )}
          <Textarea className="mt-4" placeholder="Catatan" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="kasir-notes" />
          <PrimaryBtn className="mt-4 w-full" disabled={!items.length || saving || !pic} onClick={save} data-testid="kasir-submit">{saving ? "Menyimpan…" : "Simpan Penjualan"}</PrimaryBtn>
        </div>
      </div>
    </div>
  );
}
